import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import SignedWaiversPanel from "./SignedWaiversPanel";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

// Friendly labels + accent colors per action
const ACTION_META = {
  grant_tier:           { label: "Grant Tier",       color: "text-emerald-300 border-emerald-400/30" },
  delete_member:        { label: "Delete Member",    color: "text-red-300 border-red-400/30" },
  approve_oneonone:     { label: "Approve 1-on-1",   color: "text-emerald-300 border-emerald-400/30" },
  deny_oneonone:        { label: "Deny 1-on-1",      color: "text-red-300 border-red-400/30" },
  redeem_session:       { label: "Redeem Session",   color: "text-amber-300 border-amber-400/30" },
  unredeem_session:     { label: "Unredeem Session", color: "text-zinc-300 border-zinc-600" },
  add_note:             { label: "Add Note",         color: "text-zinc-300 border-zinc-600" },
  delete_note:          { label: "Delete Note",      color: "text-red-300 border-red-400/30" },
  complete_assessment:  { label: "Mark Paid",        color: "text-emerald-300 border-emerald-400/30" },
  delete_assessment:    { label: "Delete Assessment",color: "text-red-300 border-red-400/30" },
};

export default function AuditTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/audit-log");
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit log");
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((row) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return [row.action, row.actor_email, row.target_name, row.target_id, row.detail]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div data-testid="tab-audit">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="font-display text-3xl">
          Audit Log <span className="text-zinc-500 text-base">({filtered.length})</span>
        </h2>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by action, email, target..."
          className="bg-[#141414] border border-zinc-800 px-4 py-2 text-sm w-72 focus:outline-none focus:border-[#E5B43A]"
          data-testid="audit-search"
        />
      </div>

      <p className="text-xs text-zinc-500 mb-4 leading-relaxed max-w-2xl flex items-start gap-2">
        <ShieldCheck size={14} className="text-[#E5B43A] mt-0.5 shrink-0" />
        Every consequential admin action — granting a tier, redeeming a session, approving a 1-on-1
        request, marking an assessment paid — is logged here with timestamp + admin email.
        Read-only. Last 200 actions.
      </p>

      <SignedWaiversPanel />

      <div className="border border-zinc-800 bg-[#141414] overflow-x-auto">
        <div className="max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="sticky top-0 bg-[#0A0A0A] z-10">
              <tr className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-800">
                <th className="text-left px-5 py-3 font-bold">When</th>
                <th className="text-left px-5 py-3 font-bold">Action</th>
                <th className="text-left px-5 py-3 font-bold hidden md:table-cell">Actor</th>
                <th className="text-left px-5 py-3 font-bold">Target</th>
                <th className="text-left px-5 py-3 font-bold hidden lg:table-cell">Detail</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-zinc-500">
                    {items.length === 0
                      ? "No admin actions recorded yet. Logs appear as you grant tiers, redeem sessions, etc."
                      : "No entries match your filter."}
                  </td>
                </tr>
              ) : filtered.map((row) => {
                const meta = ACTION_META[row.action] || { label: row.action, color: "text-zinc-300 border-zinc-600" };
                return (
                  <tr
                    key={row.id}
                    className="border-b border-zinc-900 hover:bg-[#1a1a1a] transition-colors"
                    data-testid={`audit-row-${row.id}`}
                  >
                    <td className="px-5 py-4 text-zinc-500 text-xs whitespace-nowrap">{fmtDate(row.created_at)}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center text-[10px] font-bold tracking-[0.2em] uppercase border px-2 py-1 ${meta.color}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-zinc-400 text-xs hidden md:table-cell whitespace-nowrap">{row.actor_email}</td>
                    <td className="px-5 py-4 text-zinc-200 break-all">{row.target_name || row.target_id || "—"}</td>
                    <td className="px-5 py-4 text-zinc-500 text-xs hidden lg:table-cell">{row.detail || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
